import React, { useEffect, useState, useCallback } from 'react';
import {
  FolderKanban,
  RefreshCw,
  AlertTriangle,
  Target,
  Users,
  Clock,
  CheckCircle2,
  Circle,
  Loader2,
  Ban,
  ChevronDown,
  ChevronRight,
  ListTodo,
  Archive,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { projectsApi } from '@/services/api/agentApi';
import * as errorUtils from '@/utils/errors';

interface ProjectStats {
  totalTasks: number;
  completedTasks: number;
  readyTasks: number;
  blockedTasks: number;
  overdueTasks: number;
  percentComplete: number;
}

interface ProjectGoal {
  goal_id: string;
  title: string;
  status: string;
  priority: string;
}

interface ProjectTask {
  task_id: string;
  goal_id: string;
  title: string;
  status: string;
  assigned_to: string;
  assigned_type: string;
  priority: string;
  due_date?: string;
}

interface ProjectData {
  project_id: string;
  name: string;
  description: string;
  status: string;
  owner_id: string;
  owner_type: string;
  members: Array<{ participant_id: string; participant_type: string; role: string }>;
  due_date?: string;
  created_at: string;
  updated_at: string;
  goals?: ProjectGoal[];
  stats?: ProjectStats;
}

interface ProjectDetail {
  project: ProjectData;
  goals: ProjectGoal[];
  tasks: ProjectTask[];
  stats: ProjectStats;
}

const statusColors: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-300',
  completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-300',
  paused: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-300',
  archived: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-300',
};

const taskStatusColors: Record<string, string> = {
  ready: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  blocked: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  pending: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500',
};

const goalStatusIcons: Record<string, React.ReactNode> = {
  completed: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />,
  in_progress: <Loader2 className="w-3.5 h-3.5 text-amber-500" />,
  not_started: <Circle className="w-3.5 h-3.5 text-gray-400" />,
  blocked: <Ban className="w-3.5 h-3.5 text-red-500" />,
};

const priorityColors: Record<string, string> = {
  critical: 'text-red-600 dark:text-red-400',
  high: 'text-amber-600 dark:text-amber-400',
  medium: 'text-blue-600 dark:text-blue-400',
  low: 'text-gray-500 dark:text-gray-400',
};

type StatusFilter = 'all' | 'active' | 'completed' | 'paused' | 'archived';

export const ProjectsTab: React.FC = () => {
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailData, setDetailData] = useState<Record<string, ProjectDetail>>({});
  const [loadingDetail, setLoadingDetail] = useState<string | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await projectsApi.list(true);
      const list = result?.data || [];
      setProjects(Array.isArray(list) ? list : []);
    } catch (err: unknown) {
      setError(errorUtils.getErrorMessageOrFallback(err, 'Failed to load projects'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const toggleExpand = async (projectId: string) => {
    if (expandedId === projectId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(projectId);

    // Fetch detail if not cached
    if (!detailData[projectId]) {
      setLoadingDetail(projectId);
      try {
        const result = await projectsApi.get(projectId);
        if (result?.data) {
          setDetailData(prev => ({ ...prev, [projectId]: result.data }));
        }
      } catch {
        // Detail fetch failed — card still shows summary stats
      } finally {
        setLoadingDetail(null);
      }
    }
  };

  const archiveProject = async (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Archive this project? It can be restored later.')) return;
    setArchivingId(projectId);
    try {
      await projectsApi.updateStatus(projectId, 'archived');
      setProjects(prev => prev.map(p =>
        p.project_id === projectId ? { ...p, status: 'archived' } : p,
      ));
      if (expandedId === projectId) setExpandedId(null);
    } catch {
      // Silently fail — project card will still show current status
    } finally {
      setArchivingId(null);
    }
  };

  const filtered = filter === 'all' ? projects : projects.filter(p => p.status === filter);

  if (loading && projects.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!loading && projects.length === 0 && !error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground p-8">
        <FolderKanban className="w-16 h-16 mb-4 opacity-40" />
        <h3 className="text-lg font-medium text-foreground mb-1">No Projects</h3>
        <p className="text-sm text-center mb-6 max-w-md">
          Create a project via chat: &quot;Create a project called [name]&quot;
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header bar */}
      <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
        <div className="flex items-center gap-2">
          <FolderKanban className="w-5 h-5 text-primary" />
          <span className="font-medium">
            {filtered.length} project{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Status filter */}
          <div className="flex gap-1">
            {(['all', 'active', 'completed', 'paused', 'archived'] as StatusFilter[]).map(f => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'ghost'}
                size="sm"
                className="text-xs h-7 px-2.5"
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </Button>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={fetchProjects} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-3 flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Project cards */}
      <ScrollArea className="flex-1">
        <div className="p-4 grid gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
          {filtered.map(project => {
            const stats = project.stats || { totalTasks: 0, completedTasks: 0, readyTasks: 0, blockedTasks: 0, overdueTasks: 0, percentComplete: 0 };
            const goals = project.goals || [];
            const isExpanded = expandedId === project.project_id;
            const detail = detailData[project.project_id];
            const isLoadingThis = loadingDetail === project.project_id;

            return (
              <div
                key={project.project_id}
                className="border rounded-lg bg-card hover:border-primary/40 transition-colors"
              >
                {/* Card header */}
                <div className="p-4 pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-sm truncate">{project.name}</h4>
                      {project.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {project.description}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className={statusColors[project.status] || ''}>
                      {project.status}
                    </Badge>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="px-4 pb-3">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                    <span>{stats.percentComplete}% complete</span>
                    <span>{stats.completedTasks}/{stats.totalTasks} tasks</span>
                  </div>
                  <Progress value={stats.percentComplete} className="h-2" />
                </div>

                {/* Stats row */}
                <div className="px-4 pb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    {goals.length} goal{goals.length !== 1 ? 's' : ''}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {project.members?.length || 1} member{(project.members?.length || 1) !== 1 ? 's' : ''}
                  </span>
                  {stats.blockedTasks > 0 && (
                    <span className="flex items-center gap-1 text-red-500">
                      <Ban className="w-3 h-3" />
                      {stats.blockedTasks} blocked
                    </span>
                  )}
                  {stats.overdueTasks > 0 && (
                    <span className="flex items-center gap-1 text-amber-500">
                      <AlertTriangle className="w-3 h-3" />
                      {stats.overdueTasks} overdue
                    </span>
                  )}
                  {project.due_date && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Due: {new Date(project.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>

                {/* Goals list */}
                {goals.length > 0 && (
                  <div className="px-4 pb-3 border-t pt-3">
                    <div className="text-xs font-medium text-muted-foreground mb-1.5">Goals</div>
                    <div className="space-y-1">
                      {goals.map((goal: ProjectGoal) => (
                        <div key={goal.goal_id} className="flex items-center gap-2 text-xs">
                          {goalStatusIcons[goal.status] || <Circle className="w-3.5 h-3.5 text-gray-400" />}
                          <span className="truncate">{goal.title}</span>
                          <span className="text-muted-foreground ml-auto whitespace-nowrap">{goal.status.replace('_', ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expand/collapse for tasks + archive */}
                <div className="px-4 py-3 border-t flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => toggleExpand(project.project_id)}
                    disabled={isLoadingThis}
                  >
                    {isLoadingThis ? (
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : isExpanded ? (
                      <ChevronDown className="w-3.5 h-3.5 mr-1.5" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {isExpanded ? 'Hide Tasks' : 'View Tasks'}
                  </Button>
                  {project.status !== 'archived' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={(e) => archiveProject(project.project_id, e)}
                      disabled={archivingId === project.project_id}
                      title="Archive project"
                    >
                      {archivingId === project.project_id ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Archive className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  )}
                </div>

                {/* Expanded task detail */}
                {isExpanded && detail && (
                  <div className="border-t bg-muted/30 px-4 py-3">
                    {detail.tasks.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-2">No tasks yet</p>
                    ) : (
                      <div className="space-y-3">
                        {detail.goals.map((goal: ProjectGoal) => {
                          const goalTasks = detail.tasks.filter((t: ProjectTask) => t.goal_id === goal.goal_id);
                          if (goalTasks.length === 0) return null;
                          return (
                            <div key={goal.goal_id}>
                              <div className="flex items-center gap-1.5 mb-1.5 text-xs font-medium">
                                <ListTodo className="w-3 h-3 text-muted-foreground" />
                                {goal.title}
                              </div>
                              <div className="space-y-1 pl-4">
                                {goalTasks.map((task: ProjectTask) => (
                                  <div key={task.task_id} className="flex items-center gap-2 text-xs">
                                    <Badge
                                      variant="secondary"
                                      className={`text-[10px] px-1.5 py-0 ${taskStatusColors[task.status] || ''}`}
                                    >
                                      {task.status.replace('_', ' ')}
                                    </Badge>
                                    <span className="truncate flex-1">{task.title}</span>
                                    {task.priority && task.priority !== 'medium' && (
                                      <span className={`text-[10px] font-medium ${priorityColors[task.priority] || ''}`}>
                                        {task.priority}
                                      </span>
                                    )}
                                    {task.due_date && (
                                      <span className="text-muted-foreground whitespace-nowrap">
                                        {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};
